// libs/shared/src/services/supabase.service.ts

import { Injectable, Inject } from '@angular/core';
import { createClient, SupabaseClient, User, AuthResponse, AuthChangeEvent, Session } from '@supabase/supabase-js';
import { SUPABASE_CONFIG, SupabaseConfig } from '../config/supabase.config';
import { MenuItem } from '../models/menu-item.model';

export interface DbResponse<T = any> {
    data: T | null;
    error: any;
}

export interface AiQuota {
    usage: number;
    limit: number;
}

@Injectable({
    providedIn: 'root'
})
export class SupabaseService {
    public readonly client: SupabaseClient;

    // --- Configuración de Analítica por Lotes ---
    private eventBuffer: any[] = [];
    private flushTimer: any = null;
    private readonly BATCH_LIMIT = 10;
    private readonly FLUSH_INTERVAL = 5000;


    constructor(@Inject(SUPABASE_CONFIG) config: SupabaseConfig) {
        this.client = createClient(config.supabaseUrl, config.supabaseKey);

        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', () => {
                if (this.eventBuffer.length > 0) {
                    this.flushEvents();
                }
            });
        }
    }

    /**
     * Devuelve el usuario actual autenticado
     */
    async getCurrentUser(): Promise<User | null> {
        try {
            const { data: { user } } = await this.client.auth.getUser();
            return user;
        } catch (error) {
            console.error('Error fetching current user:', error);
            return null;
        }
    }

    // --- Autenticación ---
    async signUp(email: string, pass: string, metadata?: any): Promise<AuthResponse> {
        return await this.client.auth.signUp({
            email,
            password: pass,
            options: { data: metadata }
        });
    }

    async signIn(email: string, pass: string): Promise<AuthResponse> {
        return await this.client.auth.signInWithPassword({ email, password: pass });
    }

    async signOut(): Promise<{ error: any }> {
        return await this.client.auth.signOut();
    }

    async updateUserMetadata(metadata: any): Promise<any> {
        return await this.client.auth.updateUser({ data: metadata });
    }

    onAuthStateChange(callback: (user: User | null) => void) {
        return this.client.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
            callback(session?.user ?? null);
        });
    }

    // --- Roles y Membresías (Seguridad y Multi-Tenancy) ---

    async getUserRole(userId: string): Promise<DbResponse> {
        try {
            return await this.client.from('user_roles').select('id, user_id, role, created_at, updated_at').eq('user_id', userId).single();
        } catch (error) {
            return { data: null, error };
        }
    }

    /**
     * Obtiene las membresías del usuario a diferentes restaurantes (Multi-Restaurante).
     */
    async getUserMemberships(userId: string): Promise<DbResponse> {
        return await this.client
            .from('restaurant_members')
            .select(`
                restaurant_id,
                role,
                restaurant:restaurants (name, slug, logo_url)
            `)
            .eq('user_id', userId);
    }

    async updateUserRole(userId: string, updates: { role: string }): Promise<DbResponse> {
        try {
            return await this.client.from('user_roles').update(updates).eq('user_id', userId);
        } catch (error) {
            return { data: null, error };
        }
    }

    // --- Restaurantes (Gestión Comercial) ---

    async getOwnerRestaurants(userId: string): Promise<DbResponse> {
        return await this.client.from('restaurants').select('*').eq('owner_id', userId);
    }

    async getAllRestaurants(): Promise<DbResponse> {
        return await this.client
            .from('restaurants')
            .select('id, name, slug, address, is_active, plan_type, created_at, logo_url') // Incluimos logo_url
            .order('name', { ascending: true });
    }

    async getRestaurantsWithItemCount(): Promise<DbResponse> {
        return await this.client
            .from('restaurants')
            .select('*, menu_items(count)')
            .order('name', { ascending: true });
    }

    async createRestaurant(profile: { name: string; slug: string; address?: string }): Promise<DbResponse> {
        const user = await this.getCurrentUser();
        if (!user) return { data: null, error: new Error('Usuario no autenticado') };

        // Paso 1: Crear el restaurante en la tabla 'restaurants'
        const res = await this.client.from('restaurants').insert({
            id: crypto.randomUUID(),
            owner_id: user.id,
            name: profile.name,
            slug: profile.slug,
            address: profile.address,
            is_active: true
        }).select().single();

        if (res.error) return res;

        // Paso 2: Crear el registro en 'restaurant_members'
        if (res.data) {
            const memberRes = await this.client.from('restaurant_members').insert({
                user_id: user.id,
                restaurant_id: res.data.id,
                role: 'propietario' // Debe ser 'propietario' según tu CHECK
            });

            if (memberRes.error) {
                console.error('Error en membresía:', memberRes.error);
            }
        }

        return res;
    }

    async deleteRestaurant(id: string): Promise<DbResponse> {
        return await this.client.from('restaurants').delete().eq('id', id).select();
    }

    async toggleRestaurantStatus(restaurantId: string, isActive: boolean): Promise<DbResponse> {
        return await this.client.from('restaurants').update({ is_active: isActive }).eq('id', restaurantId).select();
    }

    async updateSubscription(restaurantId: string, plan: 'basico' | 'pro' | 'gourmet'): Promise<DbResponse> {
        const limites = { 'basico': 30, 'pro': 100, 'gourmet': 9999 };
        return await this.client.from('restaurants').update({
            plan_type: plan,
            item_limit: limites[plan],
            subscription_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        }).eq('id', restaurantId).select();
    }

    // --- Categorías ---

    // --- Categorías ---

    async getCategories(): Promise<DbResponse> {
        return await this.client.from('categories').select('*').order('order', { ascending: true });
    }

    async createCategory(name: Record<string, string> | string): Promise<DbResponse> {
        const nameObj = typeof name === 'string' ? { en: name, es: name } : name;
        const slug = (nameObj['en'] || nameObj['es'] || 'new_category').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        return await this.client.from('categories').insert({ name: nameObj, slug, order: 999 }).select().single();
    }

    // --- Gestión de Imágenes (Storage) ---

    async uploadDishImage(restaurantId: string, itemId: string, file: Blob | File): Promise<string | null> {
        const filePath = `restaurantes/${restaurantId}/platos/${itemId}.webp`;
        try {
            // El 'upsert: true' es correcto para sobrescribir en el servidor
            const { data, error } = await this.client.storage
                .from('menu-assets')
                .upload(filePath, file, { contentType: 'image/webp', upsert: true });

            if (error) throw error;

            const { data: { publicUrl } } = this.client.storage
                .from('menu-assets')
                .getPublicUrl(filePath);

            //Añadimos un timestamp para romper el caché del navegador
            return `${publicUrl}?t=${Date.now()}`;
        } catch (err) {
            console.error('Error en Storage:', err);
            return null;
        }
    }

    // --- Items de Menú ---

    async getMenuItemsByRestaurant(restaurantId: string): Promise<DbResponse> {
        return await this.client
            .from('menu_items')
            .select('id, name, price, description, is_available, is_vegan, is_gluten_free, is_vegetarian, allergens, macros, carbon_footprint, image_url, category_id, category:categories(id, name, order)')
            .eq('restaurant_id', restaurantId)
            .order('name', { ascending: true });
    }

    async getAllMenuItemsGlobal(): Promise<DbResponse> {
        return await this.client
            .from('menu_items')
            .select('id, name, price, description, is_available, restaurant_id, is_vegan, is_gluten_free, is_vegetarian, allergens, macros, carbon_footprint, image_url, category_id, categories(id, name, order)');
    }

    async getRestaurantPerformance(): Promise<DbResponse> {
        return await this.client.from('v_restaurant_performance').select('*');
    }

    // Reemplaza el método upsertMenuItem por este:
    async upsertMenuItem(restaurantId: string, item: MenuItem): Promise<DbResponse> {
        if (!item.name || !item.category_id) throw new Error('Name and Category are required');

        const mappedItem = {
            restaurant_id: restaurantId, // Columna corregida
            name: item.name,
            category_id: item.category_id,
            price: item.price,
            description: item.description,
            allergens: item.allergens,
            macros: item.macros,
            carbon_footprint: item.carbon_footprint,
            image_url: item.image_url,
            is_vegan: item.is_vegan,
            is_gluten_free: item.is_gluten_free,
            is_vegetarian: item.is_vegetarian,
            is_available: true,
            updated_at: new Error().stack?.includes('Zone') ? undefined : new Date().toISOString()
        };

        // Búsqueda de duplicados usando el operador JSONB ->> para 'name'
        const searchName = item.name['es'] || item.name['en'];

        const { data: existing } = await this.client
            .from('menu_items')
            .select('id')
            .eq('restaurant_id', restaurantId)
            .or(`name->>es.ilike.${searchName},name->>en.ilike.${searchName}`)
            .maybeSingle();

        if (existing && !item.id) {
            return await this.client.from('menu_items').update(mappedItem).eq('id', existing.id).select();
        } else {
            return await this.client.from('menu_items').upsert({ id: item.id, ...mappedItem }).select();
        }
    }
    /**
     * Inserta múltiples items. Resuelve el error TS2551.
     */
    async bulkInsertMenuItems(restaurantId: string, items: MenuItem[]): Promise<DbResponse> {
        if (!items.length) return { data: null, error: new Error('No hay items') };
        const mappedItems = items.map(item => ({
            restaurant_id: restaurantId,
            name: item.name,
            category_id: item.category_id,
            price: item.price,
            description: item.description,
            allergens: item.allergens,
            macros: item.macros,
            carbon_footprint: item.carbon_footprint,
            image_url: item.image_url,
            is_vegan: item.is_vegan,
            is_gluten_free: item.is_gluten_free,
            is_vegetarian: item.is_vegetarian,
            is_available: true
        }));
        return await this.client.from('menu_items').insert(mappedItems);
    }

    async updateMenuItemById(id: string, updates: Partial<MenuItem>): Promise<DbResponse> {
        // Mapeo directo de propiedades ya renombradas en MenuItem
        const mappedUpdates: any = { ...updates };
        // Limpiamos conversiones viejas
        if (mappedUpdates.descripcionMarketing) delete mappedUpdates.descripcionMarketing;
        if (mappedUpdates.huellaCarbono) delete mappedUpdates.huellaCarbono;

        return await this.client.from('menu_items').update(mappedUpdates).eq('id', id).select();
    }

    async deleteMenuItem(id: string): Promise<DbResponse> {
        return await this.client.from('menu_items').delete().eq('id', id).select();
    }

    // --- Acceso Público e-Menu ---

    async getRestaurantBySlug(slug: string): Promise<DbResponse> {
        return await this.client.from('restaurants').select('id, name, slug, address, logo_url, created_at, is_verified, is_active').eq('slug', slug).single();
    }

    // libs/shared/src/services/supabase.service.ts

    async getPublicMenu(restaurantId: string): Promise<DbResponse> {
        return await this.client
            .from('menu_items')
            .select(`
            id, 
            name, 
            price, 
            description, 
            image_url, 
            allergens, 
            macros, 
            carbon_footprint, 
            is_vegan, 
            is_gluten_free, 
            is_vegetarian, 
            category:categories(id, name, icon, order) -- Alias 'category' para el join
        `)
            .eq('restaurant_id', restaurantId) // Columna en inglés
            .eq('is_available', true) // Columna en inglés
            .order('order', { referencedTable: 'categories', ascending: true });
    }

    // --- Analítica ---

    async trackEvent(data: any, immediate: boolean = false): Promise<void> {
        if (immediate) {
            this.client.auth.getSession().then(({ data: { session } }) => {
                this.client.functions.invoke('analytics-ingest', {
                    body: data,
                    headers: {
                        Authorization: `Bearer ${session?.access_token}`
                    }
                }).catch(err => console.error('Error tracking:', err));
            });
            return;
        }
        this.eventBuffer.push(data);
        if (this.eventBuffer.length >= this.BATCH_LIMIT) {
            this.flushEvents();
        } else if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => this.flushEvents(), this.FLUSH_INTERVAL);
        }
    }

    private async flushEvents() {
        if (this.eventBuffer.length === 0) return;
        const eventsToSend = [...this.eventBuffer];
        this.eventBuffer = [];
        this.flushTimer = null;

        const { data: { session } } = await this.client.auth.getSession();
        this.client.functions.invoke('analytics-ingest', {
            body: { events: eventsToSend },
            headers: {
                Authorization: `Bearer ${session?.access_token}`
            }
        }).catch(err => console.error('Error batching:', err));
    }

    async testConnection(): Promise<boolean> {
        try {
            const { error } = await this.client.from('restaurants').select('id').limit(1);
            return !error;
        } catch (error) {
            console.error('Supabase Connection Error:', error);
            return false;
        }
    }


    async getMonthlyAiUsage(restaurantId: string): Promise<AiQuota> {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        // 1. Obtenemos el límite dinámico del restaurante
        const { data: restData, error: restError } = await this.client
            .from('restaurants')
            .select('monthly_ai_scan_limit')
            .eq('id', restaurantId)
            .single();

        if (restError) throw new Error(restError.message);

        // 2. Contamos los logs SUCCESS del mes actual
        const { count, error: countError } = await this.client
            .from('ai_usage_logs')
            .select('*', { count: 'exact', head: true })
            .eq('restaurant_id', restaurantId)
            .eq('status', 'SUCCESS')
            .gte('created_at', firstDayOfMonth);

        if (countError) throw new Error(countError.message);

        return {
            usage: count || 0,
            limit: restData?.monthly_ai_scan_limit ?? 0 // Usamos el valor de la tabla
        };
    }

    async logAiUsage(params: any) {
        const { data: { user } } = await this.client.auth.getUser();
        if (!user) return;

        const { error } = await this.client
            .from('ai_usage_logs')
            .insert([{
                ...params,
                user_id: user.id,
                provider: 'groq'
            }]);

        if (error) console.error('Error log:', error);
    }
    /**
     * Dispara el flujo de recuperación de contraseña enviando un email al usuario.
     */
    async resetPassword(email: string): Promise<DbResponse> {
        const redirectTo = `${window.location.origin}/auth?type=recovery`;
        return await this.client.auth.resetPasswordForEmail(email, {
            redirectTo,
        });
    }

    /**
     * Actualiza la contraseña del usuario autenticado (usado tras volver del email).
     */
    async updatePassword(newPassword: string): Promise<DbResponse> {
        return await this.client.auth.updateUser({
            password: newPassword
        });
    }

    /**
     * Sube un nuevo logo al storage y actualiza el registro en la base de datos.
     * @param restaurantId ID del restaurante.
     * @param file Archivo de imagen (Blob o File).
     * @returns URL pública del nuevo logo o null si hubo error.
     */
    async updateRestaurantLogo(restaurantId: string, file: Blob | File): Promise<string | null> {
        const filePath = `restaurantes/${restaurantId}/brand/logo_main.webp`;
        try {
            // 1. Subida física al Storage (sobrescribiendo el anterior)
            const { error: uploadError } = await this.client.storage
                .from('menu-assets')
                .upload(filePath, file, { contentType: 'image/webp', upsert: true });

            if (uploadError) throw uploadError;

            // 2. Obtención de URL pública
            const { data: { publicUrl } } = this.client.storage
                .from('menu-assets')
                .getPublicUrl(filePath);

            // 3. Persistencia en la tabla restaurants
            const { error: dbError } = await this.client
                .from('restaurants')
                .update({ logo_url: publicUrl })
                .eq('id', restaurantId);

            if (dbError) throw dbError;

            return `${publicUrl}?t=${Date.now()}`;
        } catch (err) {
            console.error('Error actualizando logo:', err);
            return null;
        }
    }

    /**
     * Elimina el logo del restaurante.
     * @param restaurantId ID del restaurante.
     * @returns true si se eliminó correctamente, false en caso contrario.
     */
    async deleteRestaurantLogo(restaurantId: string): Promise<boolean> {
        try {
            // 1. Limpiamos la referencia en la tabla de restaurantes
            const { error: dbError } = await this.client
                .from('restaurants')
                .update({ logo_url: null })
                .eq('id', restaurantId);

            if (dbError) throw dbError;

            // 2. Opcional: Borrar el archivo físico del bucket para ahorrar espacio
            const filePath = `restaurantes/${restaurantId}/brand/logo_main.webp`;
            await this.client.storage.from('menu-assets').remove([filePath]);

            return true;
        } catch (err) {
            console.error('Error al eliminar logo:', err);
            return false;
        }
    }

}